import React, { useEffect, useState, memo } from 'react';
import { has } from 'lodash';
import styled from 'styled-components/macro';
import { TextField } from '@material-ui/core';

import AutoComplete from 'components/ui/AutoComplete';
import SchedulerLinkField from 'components/general/SchedulerLinkField';
import { getSchedulersByOrganizationId } from 'api/scheduler';
import { formatParticipationRate } from 'helpers/format-string';

const Details = styled.div``;

const SchedulerHeading = styled.h5`
  margin: 2em 0 0.5em;
`;

const SchedulerFieldGroup = styled.div`
  margin-bottom: 1.5em;
`;

const ClinicsCount = styled.h5`
  margin-bottom: 1.5em;
`;

const Detail = styled.p`
  margin-bottom: 0.25em;
`;

const Overview = styled.div`
  display: flex;
  margin: 1.5em 0 1.5em;
`;

const OverviewDetails = styled.div`
  margin-right: 3em;
`;

const DetailsTitle = styled.h5`
  margin-bottom: 1.5em;
`;

const Scheduler = memo(
  ({
    scheduler,
    schedulerId,
    touched,
    errors,
    setFieldValue,
    concurrentSlots,
    handleChange,
    recommendedNurses,
    organizationId,
    recommendedTotalSlots,
    timeSlots,
  }) => {
    const [schedulers, setSchedulers] = useState([]);

    const recommendedConcurrentSlots = recommendedNurses;

    const recommendedSlotSuffix =
      recommendedConcurrentSlots > 1 ? 'slots' : 'slot';
    const timeSlotsSuffix = Math.floor(timeSlots) > 1 ? 'slots' : 'slot';
    const recommendedTotalSlotsSuffix =
      Math.floor(recommendedTotalSlots) > 1 ? 'slots' : 'slot';

    useEffect(async () => {
      const { data } = await getSchedulersByOrganizationId(organizationId);

      setSchedulers(data.data);
    }, [organizationId]);

    const {
      link,
      id,
      booked,
      slots,
      clinics_number: clinicsCount,
      rate_participation: rateParticipation,
    } = scheduler || {};

    return (
      <>
        <SchedulerFieldGroup>
          <AutoComplete
            data={schedulers || []}
            label="Scheduler"
            name="schedulerId"
            setFieldValue={setFieldValue}
            value={schedulerId}
            groupBy="name"
            getOptionLabel={({ name }) => name || ''}
            selection={scheduler}
            error={touched.schedulerId && has(errors, 'schedulerId')}
          />

          {schedulerId && (
            <>
              <SchedulerHeading>Scheduler ID - {id}</SchedulerHeading>
              <ClinicsCount>{clinicsCount} Clinics</ClinicsCount>

              <Detail>{slots} slots</Detail>
              <Detail>{booked} booked</Detail>
              <Detail>{slots - booked} vacancies</Detail>
              <Detail>
                {formatParticipationRate(rateParticipation)} participation
              </Detail>
            </>
          )}
        </SchedulerFieldGroup>

        <div>
          <TextField
            label="Concurrent Slots"
            name="concurrentSlots"
            fullWidth
            InputProps={{ inputProps: { min: 1 } }}
            error={touched.concurrentSlots && has(errors, 'concurrentSlots')}
            helperText={touched.concurrentSlots && errors.concurrentSlots}
            onChange={handleChange}
            variant="outlined"
            value={concurrentSlots}
            type="number"
            required
          />

          <Overview>
            <OverviewDetails>
              <DetailsTitle>Recommended</DetailsTitle>
              <Detail>
                {recommendedConcurrentSlots} concurrent {recommendedSlotSuffix}
              </Detail>
              <Detail>
                {Math.floor(recommendedTotalSlots)} total{' '}
                {recommendedTotalSlotsSuffix}
              </Detail>
            </OverviewDetails>

            <OverviewDetails>
              <DetailsTitle>Actual Available</DetailsTitle>
              <Detail>
                {Math.floor(timeSlots)} {timeSlotsSuffix}
              </Detail>
            </OverviewDetails>
          </Overview>
        </div>
        {schedulerId && <SchedulerLinkField link={link} />}

        <Details />
      </>
    );
  },
);

export default Scheduler;
